<script setup lang="ts">
import { Plus as PlusIcon } from '@element-plus/icons-vue'
import {
  BoxComponent,
  ButtonComponent,
  type CampaignListItem,
  InputComponent,
  PageWrapperComponent,
  PaginationComponent,
} from '@metapic-apps/shared'
import { ElButton, ElDialog, ElIcon } from 'element-plus'
import debounce from 'lodash/debounce'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { useDeleteCampaign } from '@/api/campaign.api'
import { useGetCampaigns } from '@/api/campaigns.api'
import { useGetNotificationStatuses } from '@/api/notification-subscriptions.api'
import SearchIcon from '@/assets/icons/Magnifier.svg?component'
import CampaignCard from '@/components/campaign-card/CampaignCard.vue'
import CampaignOverview from '@/components/CampaignOverview/CampaignOverview.vue'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { FlexComponent, LoadingComponent } from '@/ui-library'

const route = useRoute()
const router = useRouter()
const store = useStore()

const isCampaignOverviewOpen = ref(false)

watch(
  () => route.query.campaignId,
  (campaignId) => {
    isCampaignOverviewOpen.value = Boolean(campaignId)
  },
  { immediate: true },
)

const onboardingStep = computed(() => store.getters['onboarding/currentOnboardingStep'])
const hideEditCampaign = computed(() => store.getters['shop/hideEdit'])
const advertiserStore = useAdvertiserStore()
const advertiser = computed(() => advertiserStore?.advertiser)

const page = ref(1)
const searchQuery = ref('')
const searchInput = ref('')
const removeCampaignId = ref<number | null>(null)

const applySearchQuery = debounce((value: string) => {
  if (value.length > 1 || value.length === 0) {
    page.value = 1
    searchQuery.value = value
  }
}, 500)

const handleSearchInput = (value: string) => {
  searchInput.value = value
  applySearchQuery(value)
}

const { data: campaigns, isLoading, totalPages, fetchCampaigns } = useGetCampaigns(advertiser, page, searchQuery)

const selectedCampaign = computed(() => campaigns.value?.find((campaign) => campaign.id === Number(route.query.campaignId)))

const { deleteCampaign, isLoading: isDeleteCampaignLoading } = useDeleteCampaign()

const subjects = computed(() => (campaigns.value ? campaigns.value.map((campaign) => `campaign:${campaign.id}`) : []))
const { hasUpdates } = useGetNotificationStatuses(subjects)

const showRemoveDialog = (id: number) => {
  removeCampaignId.value = id
}

const removeCampaign = async () => {
  if (removeCampaignId.value) {
    await deleteCampaign(removeCampaignId.value)
  }
  page.value = 1
  removeCampaignId.value = null
  await fetchCampaigns()
}

const openAddCampaign = () => {
  return router.push({ name: 'campaign-creation' })
}

const openCampaignOverview = (campaign: CampaignListItem) => {
  return router.push({ query: { ...route.query, campaignId: campaign.id } })
}

onMounted(async () => {
  if (onboardingStep.value === 2) {
    await store.dispatch('onboarding/setCurrentCard', 'campaign-welcome-section')
    await store.dispatch('onboarding/setOnboardingActivity', true)
  }
  if (onboardingStep.value === 3) {
    await store.dispatch('onboarding/setCurrentCard', 'manage-campaign-start-section')
    await store.dispatch('onboarding/setOnboardingActivity', true)
  }
})

onBeforeUnmount(() => {
  applySearchQuery.cancel()
})
</script>

<template>
  <div>
    <PageWrapperComponent class="offers-page">
      <div id="manage-campaign-start-section" class="d-flex justify-content-between">
        <BoxComponent :w="240" class="campaigns__search">
          <InputComponent
            :model-value="searchInput"
            name="campaignsSearch"
            placeholder="Search campaigns"
            size="large"
            @input="handleSearchInput"
          >
            <template #prefix>
              <ElIcon :size="16">
                <SearchIcon />
              </ElIcon>
            </template>
          </InputComponent>
        </BoxComponent>
        <ButtonComponent
          v-if="!hideEditCampaign"
          id="campaign-welcome-section"
          variant="accent"
          class="campaigns__create-button"
          @click="openAddCampaign"
        >
          <template #iconLeft>
            <ElIcon :size="16">
              <PlusIcon />
            </ElIcon>
          </template>
          Create campaign
        </ButtonComponent>
      </div>
      <template v-if="campaigns?.length">
        <FlexComponent direction="column" :gap="8">
          <CampaignCard
            v-for="(campaign, index) in campaigns"
            :key="index"
            :campaign="campaign"
            :has-updates="hasUpdates(`campaign:${campaign.id}`)"
            @open="openCampaignOverview(campaign)"
          />
        </FlexComponent>
        <div class="d-flex justify-content-center mb-4">
          <PaginationComponent v-model:current-page="page" :total-pages="totalPages" class="pagination-control" />
        </div>
      </template>
      <img v-if="isLoading" src="../assets/loading.svg" alt="loading" class="loading-spinner" />
    </PageWrapperComponent>
    <ElDialog :model-value="Boolean(removeCampaignId)" :show-close="false" class="delete-user-modal">
      <template #header>
        <h5>Are you sure you want to delete this campaign?</h5>
      </template>
      <template #footer>
        <div class="d-flex justify-content-between">
          <ElButton round class="white-btn" @click="() => (removeCampaignId = null)"> Cancel </ElButton>
          <ElButton v-if="!isDeleteCampaignLoading" round class="blue-btn" @click="removeCampaign"> Yes </ElButton>
          <LoadingComponent v-if="isDeleteCampaignLoading" />
        </div>
      </template>
    </ElDialog>
    <CampaignOverview
      v-if="isCampaignOverviewOpen && selectedCampaign"
      v-model="isCampaignOverviewOpen"
      :campaign-id="selectedCampaign.id"
      :editable="!hideEditCampaign"
      @delete="showRemoveDialog"
    />
  </div>
</template>

<style scoped lang="scss">
.offers-page {
  padding-bottom: 20px;
  min-width: 420px;

  .campaigns__create-button {
    flex: none;
  }

  .campaigns-popup-tag-wrapper {
    position: absolute;
    z-index: 100;
    right: 270px;
    margin-top: -50px;
    @media screen and (max-width: 767px) {
      right: 30px;
      margin-top: 50px;
    }
  }
  .campaigns-popup-tag-wrapper-2 {
    position: absolute;
    z-index: 100;
    margin-top: 100px;
    margin-left: 350px;
  }
  .campaigns-popup-tag-wrapper-3 {
    position: absolute;
    z-index: 100;
    margin-top: -90px;
    left: 80px;
    right: 0;
    display: flex;
    justify-content: center;
  }

  .pagination-control {
    width: fit-content;
    margin: 32px auto 0;
  }

  .campaigns__search {
    margin: 4px 40px 24px 0;
    :deep(.el-input__wrapper) {
      box-shadow: none;
      background: transparent;
      padding-inline: 0;
      border-bottom: 1px solid transparent;
      border-radius: 0;
      transition: border-color var(--transition) ease;

      &:hover {
        box-shadow: none;
      }
    }

    &:focus-within :deep(.el-input__wrapper) {
      border-bottom-color: var(--black, #000);
    }
  }

  .loading-spinner {
    position: absolute;
    top: calc(50% + 32px);
    right: calc(50% - 32px);
    width: 64px;
  }

  .delete-user-modal {
    .el-dialog {
      min-width: 300px;
    }

    .el-dialog__header {
      height: 89px;
      background-image: url('../assets/create-offer-title-background.png');
    }

    .title {
      color: var(--white);
    }
  }
}
</style>
